import { SupportedLanguages } from "@/enums/internationalization";
import { VoiceOption } from "@/types/agencies";

/**
 * Compound catalog accents collapse to a simple nationality adjective
 * (African American → American, American Southern → American).
 */
const SIMPLE_ACCENT_ALIASES: Record<string, string> = {
  african_american: "american",
  american_southern: "american",
};

/**
 * Fallback accent labels when no translator is provided.
 * These are nationality / origin adjectives (American, Mexican, Colombian),
 * not country names — "Female · United States" reads as a location, not a voice.
 */
export const ACCENT_REGION_MAP: Record<string, string> = {
  american: "American",
  british: "British",
  spain: "Spanish",
  colombian: "Colombian",
  dominican: "Dominican",
  mexican: "Mexican",
  canadian: "Canadian",
  argentinian: "Argentinian",
  australian: "Australian",
  brazilian: "Brazilian",
  chilean: "Chilean",
  costa_rican: "Costa Rican",
  ecuadorian: "Ecuadorian",
  el_salvadoran: "Salvadoran",
  guatemalan: "Guatemalan",
  honduran: "Honduran",
  nicaraguan: "Nicaraguan",
  panamanian: "Panamanian",
  paraguayan: "Paraguayan",
  peruvian: "Peruvian",
  puerto_rican: "Puerto Rican",
  uruguayan: "Uruguayan",
  venezuelan: "Venezuelan",
  german: "German",
  chinese: "Chinese",
  french: "French",
  italian: "Italian",
  japanese: "Japanese",
  russian: "Russian",
  korean: "Korean",
  catalan: "Catalan",
  polish: "Polish",
  norwegian: "Norwegian",
  estonian: "Estonian",
  lithuanian: "Lithuanian",
  swedish: "Swedish",
  indian: "Indian",
  indonesian: "Indonesian",
};

function capitalizeLabel(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatAccentLabel(
  rawAccent: string,
  translateSettings?: (key: string) => string
): string {
  const simpleAccent = SIMPLE_ACCENT_ALIASES[rawAccent] ?? rawAccent;
  if (translateSettings) {
    return capitalizeLabel(translateSettings(simpleAccent));
  }
  return ACCENT_REGION_MAP[simpleAccent] ?? simpleAccent;
}

/**
 * Formats a clean subtitle for the voice card: e.g. "Female · American"
 */
export function formatVoiceSubtitle(
  voice: VoiceOption,
  translateSettings?: (key: string) => string
): string {
  const parts: string[] = [];

  if (voice.labels?.gender) {
    const rawGender = voice.labels.gender;
    const translated = translateSettings ? translateSettings(rawGender) : rawGender;
    parts.push(capitalizeLabel(translated.toLowerCase()));
  }

  if (voice.labels?.accent) {
    parts.push(formatAccentLabel(voice.labels.accent.toLowerCase(), translateSettings));
  }

  return parts.join(" · ");
}

/**
 * Formats spoken languages line: e.g. "Speaks English" or "Speaks Spanish, English +1"
 */
export function formatSpokenLanguages(
  languages: readonly SupportedLanguages[] | undefined,
  translateCommon?: (key: string) => string
): { display: string; hasExtra: boolean; extraCount: number } {
  if (!languages || languages.length === 0) {
    return { display: "", hasExtra: false, extraCount: 0 };
  }

  const translatedNames = languages.map((lang) => {
    if (translateCommon) {
      return translateCommon(lang);
    }
    switch (lang) {
      case SupportedLanguages.English:
        return "English";
      case SupportedLanguages.Spanish:
        return "Spanish";
      case SupportedLanguages.Japanese:
        return "Japanese";
      case SupportedLanguages.Portuguese:
        return "Portuguese";
      case SupportedLanguages.German:
        return "German";
      case SupportedLanguages.French:
        return "French";
      case SupportedLanguages.Italian:
        return "Italian";
      case SupportedLanguages.Chinese:
        return "Chinese";
      case SupportedLanguages.Korean:
        return "Korean";
      default:
        return lang.toUpperCase();
    }
  });

  if (translatedNames.length <= 2) {
    return {
      display: translatedNames.join(", "),
      hasExtra: false,
      extraCount: 0,
    };
  }

  const firstTwo = translatedNames.slice(0, 2).join(", ");
  const extraCount = translatedNames.length - 2;

  return {
    display: `${firstTwo} +${extraCount}`,
    hasExtra: true,
    extraCount,
  };
}
